import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { SEO } from '@/components/SEO';
import { Mail } from 'lucide-react';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO 
        pageType="custom"
        title="Privacy Policy | GoCompareAI"
        description="Read how GoCompareAI handles personal data, privacy, cookies, and user information in a transparent and responsible way."
      />
      <Header />
      
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-8">
            Privacy Policy
          </h1>
          
          <div className="prose prose-slate max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">Introduction</h2>
              <p className="text-muted-foreground leading-relaxed">
                GoCompareAI respects your privacy. This policy explains how we collect, use, and protect information when you use our website and services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">Information We Collect</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">We may collect:</p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Basic contact information when you contact us</li>
                <li>Usage data such as page views and interactions</li>
                <li>Information submitted through forms (e.g. vendor requests)</li>
                <li>Search queries entered into our AI assistant feature</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                We do not require users to create an account to use comparison features.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">AI Search Assistant</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Our AI-powered search feature allows you to describe what you're looking for in natural language. When you use this feature:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Your query is processed to match you with relevant AI platforms</li>
                <li>Queries are sent to third-party AI services (OpenAI) for processing</li>
                <li>We do not store your search queries for general searches</li>
                <li>Queries that violate our content policy may be logged for review (see Content Moderation below)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">Content Moderation</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                To maintain a safe platform, search queries are automatically checked for potentially harmful content using OpenAI's Moderation API. This includes checking for:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Harassment or threatening content</li>
                <li>Hate speech</li>
                <li>Self-harm related content</li>
                <li>Sexual or violent content</li>
                <li>Requests for illegal activities</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                Queries flagged by our moderation system are logged for administrative review to improve our safety measures. These logs may include the query text and timestamp, but do not include personally identifiable information unless you have provided it in the query itself.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">How We Use Information</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">Information is used to:</p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Operate and improve the platform</li>
                <li>Respond to enquiries</li>
                <li>Understand how the site is used</li>
                <li>Support vendor and admin workflows</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">AI Platform Data</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Information shown about AI platforms is either:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Vendor-provided, or</li>
                <li>Independently assessed by GoCompareAI</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                This information is shown for informational purposes only.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">Cookies & Analytics</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may use cookies and analytics tools to understand site usage and improve performance. These do not identify you personally.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">Data Sharing</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We do not sell personal data. Data may be shared in the following circumstances:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>With OpenAI for AI search processing and content moderation</li>
                <li>Where required to operate the service</li>
                <li>To comply with legal obligations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">Third-Party Services</h2>
              <p className="text-muted-foreground leading-relaxed">
                We use OpenAI's services to power our AI search assistant and content moderation. When you use the AI search feature, your query is transmitted to OpenAI for processing. OpenAI's use of this data is governed by their own privacy policy, which can be found at{' '}
                <a href="https://openai.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  openai.com/privacy
                </a>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">Data Security</h2>
              <p className="text-muted-foreground leading-relaxed">
                We take reasonable steps to protect information, but no online service can guarantee absolute security.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">Contact</h2>
              <p className="text-muted-foreground leading-relaxed">
                If you have questions about this policy, contact:
              </p>
              <a 
                href="mailto:info@gocompareai.com" 
                className="inline-flex items-center gap-2 text-primary hover:underline mt-2"
              >
                <Mail className="w-4 h-4" />
                info@gocompareai.com
              </a>
            </section>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
